import { useState } from 'react';
import {
  formatPhoneNumber,
  type PhoneIdentity,
  type SalesforceAccountCard,
  type SalesforceMatch,
} from '@avslack/shared';
import { useQueryClient } from '@tanstack/react-query';
import { savePhoneContact, searchSfCustomers } from '../lib/api';
import { phoneContactsKey, phoneIdentityKey } from '../data/phone';
import { Icon } from './Icon';

/**
 * KI HIV? - a SALESFORCE ugyfelkartyaja (Salesforce-os kollégáknak, docs/24).
 *
 * Ugyanaz a negy allapot, mint a TT-kartyanal (lasd CustomerCard.tsx): toltes,
 * kartya, valasztas tobb talalat kozul, ismeretlen szam - es kulon a
 * "nem valaszol". A Salesforce-ba NEM irunk: a kezi hozzarendeles az AVSlack
 * cimjegyzekeben marad.
 */
export function SalesforceIdentity({
  number,
  data,
  compact,
  assignable,
}: {
  number: string;
  data: PhoneIdentity;
  compact?: boolean;
  assignable: boolean;
}) {
  if (data.sfDown) {
    return (
      <p className="text-xs text-ink-muted">
        A Salesforce most nem válaszol – az ügyféladatok kimaradnak. A hívás ettől működik.
      </p>
    );
  }
  if (data.sfCard) return <SalesforceCardBody card={data.sfCard} compact={compact} />;
  if (data.sfMatches.length > 0) {
    return (
      <>
        <p className="mb-2 text-xs text-ink-muted">
          {assignable
            ? 'Több Salesforce-ügyfélhez is tartozik ez a szám. Melyik hív?'
            : 'Több Salesforce-ügyfélhez is tartozik ez a szám:'}
        </p>
        <SalesforceMatchList
          number={number}
          matches={data.sfMatches}
          assignable={assignable}
          keepName={data.name}
        />
      </>
    );
  }
  const known = [data.name, data.companyName].filter(Boolean).join(' · ');
  // flex-wrap: a megnyitott kereso a szoveg ala kerul, nem nyomodik ossze mellette.
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="min-w-0 text-xs text-ink-muted">
        {known ? (
          <>
            <span className="font-medium text-ink">{known}</span> · {formatPhoneNumber(number)}
          </>
        ) : (
          `Ismeretlen szám – nincs a Salesforce-ban: ${formatPhoneNumber(number)}`
        )}
      </p>
      {assignable && <SalesforceAssign number={number} name={data.name} />}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-xs">
      <span className="w-24 shrink-0 text-ink-faint">{label}</span>
      <span className="min-w-0 flex-1 break-words text-ink">{value}</span>
    </div>
  );
}

function SalesforceCardBody({ card, compact }: { card: SalesforceAccountCard; compact?: boolean }) {
  const [allPhones, setAllPhones] = useState(false);
  const phones = allPhones ? card.phones : card.phones.slice(0, 2);
  const kind = [card.type, card.group].filter(Boolean).join(' · ');

  return (
    <div>
      <div className="mb-2 flex items-start gap-2">
        <Icon name="cimjegyzek" className="mt-0.5 size-4 shrink-0 text-ink-faint" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink">{card.name}</p>
          {kind && <p className="truncate text-xs text-ink-muted">{kind}</p>}
        </div>
        <a
          href={card.url}
          target="_blank"
          rel="noreferrer"
          title="Megnyitás a Salesforce-ban"
          className="shrink-0 rounded-control border border-border px-2 py-0.5 text-[11px] font-medium text-ink-muted hover:bg-surface hover:text-ink"
        >
          Salesforce ↗
        </a>
      </div>

      <div className="space-y-1">
        <Row label="Település" value={card.city} />
        {!compact && <Row label="Cím" value={card.address} />}
        {!compact && <Row label="Praxis" value={card.practiceType} />}
        <Row label="Vevőszolgálat" value={card.customerServiceOwner} />
        <Row label="Tulajdonos" value={card.owner} />
        {!compact && <Row label="Pet felelős" value={card.petOwner} />}
        {!compact && <Row label="Vettech felelős" value={card.vetOwner} />}
        <Row label="E-mail" value={card.email} />
        {phones.map((phone) => (
          <Row key={phone.number} label={phone.label} value={formatPhoneNumber(phone.number)} />
        ))}
        {card.phones.length > 2 && (
          <button
            type="button"
            onClick={() => setAllPhones((open) => !open)}
            className="text-[11px] text-ink-muted hover:text-ink"
          >
            {allPhones ? 'Kevesebb szám' : `További számok (${card.phones.length - 2})`}
          </button>
        )}
      </div>
    </div>
  );
}

/** A talalatok kozul a kolléga valaszt - a valasztas az AVSlack cimjegyzekeben marad. */
function SalesforceMatchList({
  number,
  matches,
  assignable,
  keepName = null,
  onPicked,
}: {
  number: string;
  matches: SalesforceMatch[];
  assignable: boolean;
  keepName?: string | null;
  onPicked?: () => void;
}) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const pick = async (match: SalesforceMatch) => {
    setSaving(match.accountId);
    setError(false);
    try {
      await savePhoneContact(number, keepName ?? '', {
        companyName: match.name,
        sfAccountId: match.accountId,
      });
      await queryClient.invalidateQueries({ queryKey: phoneContactsKey });
      await queryClient.invalidateQueries({ queryKey: phoneIdentityKey(number) });
      onPicked?.();
    } catch {
      setError(true);
    } finally {
      setSaving(null);
    }
  };

  const label = (match: SalesforceMatch) => (
    <>
      <span className="block truncate text-xs font-medium text-ink">{match.name}</span>
      <span className="block truncate text-[11px] text-ink-faint">
        {[match.city, match.type].filter(Boolean).join(' · ')}
      </span>
    </>
  );

  return (
    <>
      <ul className="space-y-1">
        {matches.map((match) => (
          <li key={match.accountId}>
            {assignable ? (
              <button
                type="button"
                disabled={saving !== null}
                onClick={() => void pick(match)}
                className="w-full rounded-control border border-border px-2 py-1.5 text-left hover:bg-surface disabled:opacity-50"
              >
                {label(match)}
              </button>
            ) : (
              <div className="rounded-control border border-border-soft px-2 py-1.5">
                {label(match)}
              </div>
            )}
          </li>
        ))}
      </ul>
      {error && (
        <p role="alert" className="mt-1.5 text-xs text-danger">
          Nem sikerült menteni a hozzárendelést. Próbáld újra.
        </p>
      )}
    </>
  );
}

/**
 * "Ugyfelhez kotom" - Salesforce-os valtozat: keresés a Salesforce-ban, a
 * valasztas az AVSlack cimjegyzekebe kerul (a Salesforce-ba nem irunk).
 */
export function SalesforceAssign({
  number,
  name,
  onDone,
}: {
  number: string;
  name?: string | null;
  onDone?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<SalesforceMatch[]>([]);
  const [state, setState] = useState<'idle' | 'searching' | 'down' | 'empty'>('idle');

  const search = async () => {
    if (query.trim().length < 3) return;
    setState('searching');
    const result = await searchSfCustomers(query.trim()).catch(() => null);
    if (!result) return setState('down');
    setMatches(result.matches);
    setState(result.sfDown ? 'down' : result.matches.length === 0 ? 'empty' : 'idle');
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="shrink-0 rounded-control border border-border px-2 py-1 text-xs font-medium text-ink hover:bg-surface"
      >
        Ügyfélhez kötöm
      </button>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center gap-2">
        <input
          aria-label="Ügyfél keresése a Salesforce-ban"
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && void search()}
          placeholder="Ügyfél neve…"
          className="min-w-0 flex-1 rounded-control border border-border bg-surface px-2 py-1 text-xs"
        />
        <button
          type="button"
          onClick={() => void search()}
          disabled={query.trim().length < 3}
          className="shrink-0 rounded-control bg-accent px-2 py-1 text-xs font-semibold text-accent-ink disabled:opacity-50"
        >
          Keresés
        </button>
      </div>
      <div role="status">
        {state === 'searching' && <p className="mt-1.5 text-xs text-ink-muted">Keresés…</p>}
        {state === 'empty' && <p className="mt-1.5 text-xs text-ink-muted">Nincs találat.</p>}
        {state === 'down' && (
          <p className="mt-1.5 text-xs text-ink-muted">A Salesforce most nem válaszol.</p>
        )}
      </div>
      {matches.length > 0 && (
        <div className="mt-2">
          <SalesforceMatchList
            number={number}
            matches={matches}
            assignable
            keepName={name ?? null}
            onPicked={onDone}
          />
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="mt-2 text-xs text-ink-muted hover:text-ink"
      >
        Mégse
      </button>
    </div>
  );
}
